import React from 'react';
import CloseIcon from 'components/pure/utils/icon/CloseIcon';
import MaxLengthCounter from './MaxLengthCounter';
import classNames from 'classnames';
import './InputField.styl';

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  focused?: boolean;
  autoFocus?: boolean;
  type?: 'number' | 'text';
  max?: number;
  min?: number;
  onChange: (e: string | number) => void;
  onBlur?: () => void;
  onError?: (error: any) => void;
  value?: string | number;
  maxLength?: number;
  showClearButton?: boolean;
  className?: string;
  inputClassName?: string;
  inputRef?: (ref: HTMLInputElement) => void;
}

export default class InputField extends React.PureComponent<Props> {
  inputRef: HTMLInputElement;

  componentDidMount() {
    // the html 5 version of this prop is 'autofocus' so use that going forward
    if (this.props.focused || this.props.autoFocus) {
      this.focus();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.autoFocus && this.props.autoFocus) {
      this.focus();
    }
  }

  focus() {
    this.inputRef.focus();
    if (this.props.type !== 'number') {
      this.inputRef.selectionEnd = this.inputRef.value.length;
    }
  }

  handleChange = event => {
    const {type, max, min} = this.props;
    let {value} = event.target;

    // html type='number' inputs should handle these below cases
    // for us but if the user types the input in, it does a terrible
    // job of handling it
    if (type === 'number') {
      value = parseFloat(value);

      if (max) {
        value = Math.min(value, max);
      }

      if (min) {
        value = Math.max(value, min);
      }
    }

    this.props.onChange(value);
  };

  render() {
    const {
      // eslint-disable-next-line
      focused,
      // eslint-disable-next-line
      onChange,
      onBlur,
      // eslint-disable-next-line
      onError,
      inputRef,
      value,
      maxLength,
      showClearButton,
      className,
      inputClassName,
      ...extraProps
    } = this.props;

    const divClassname = classNames(className, 'ev-input-field ev-input', {
      'max-length-visible': maxLength && value && value.toString().length > 0,
      'clear-button-visible': showClearButton,
    });

    return (
      <div className={divClassname}>
        <input
          ref={ref => {
            if (!ref) return;

            inputRef && inputRef(ref);
            this.inputRef = ref;
          }}
          className={classNames('field-input', inputClassName)}
          onChange={this.handleChange}
          onBlur={onBlur}
          value={value}
          maxLength={maxLength}
          {...extraProps}
        />
        <MaxLengthCounter maxLength={maxLength} value={value} />
        {showClearButton && value && (
          <CloseIcon className="clear-button" onClick={() => this.props.onChange('')} />
        )}
      </div>
    );
  }
}
